import { MGPOptional, Utils } from '@everyboard/lib';
import { BooleanConfig } from '../../../config/BooleanConfig';
import { NumberConfig } from '../../../config/NumberConfig';
import { RulesConfigDescription } from '../../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../../config/RulesConfigDescriptionLocalizable';
import { Coord } from '../../../jscaip/Coord';
import { Player } from '../../../jscaip/Player';
import { TableUtils } from '../../../jscaip/TableUtils';
import { MGPValidators } from '../../../utils/MGPValidator';
import { MancalaRules } from '../common/MancalaRules';
export class AwaleRules extends MancalaRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Awalé`,
        config: {
            feedOriginalHouse: new BooleanConfig(false, MancalaRules.FEED_ORIGINAL_HOUSE),
            mustFeed: new BooleanConfig(true, MancalaRules.MUST_FEED),
            passByPlayerStore: new BooleanConfig(false, MancalaRules.PASS_BY_PLAYER_STORE),
            mustContinueDistributionAfterStore: new BooleanConfig(false, MancalaRules.MULTIPLE_SOW),
            continueLapUntilCaptureOrEmptyHouse: new BooleanConfig(false, MancalaRules.CYCLICAL_LAP),
            seedsByHouse: new NumberConfig(4, MancalaRules.SEEDS_BY_HOUSE, MGPValidators.range(1, 99)),
            width: new NumberConfig(6, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(1, 99)),
            numberOfRows: new NumberConfig(1, MancalaRules.NUMBER_OF_ROWS, MGPValidators.range(1, 99)),
        },
    });
    static get() {
        if (AwaleRules.singleton.isAbsent()) {
            AwaleRules.singleton = MGPOptional.of(new AwaleRules([2, 3]));
        }
        return AwaleRules.singleton.get();
    }
    getRulesConfigDescription() {
        return AwaleRules.RULES_CONFIG_DESCRIPTION;
    }
    applyCapture(distributionResult, config) {
        const filledCoords = distributionResult.filledCoords;
        const landingCoord = filledCoords[filledCoords.length - 1];
        const resultingState = distributionResult.resultingState;
        return this.captureIfLegal(landingCoord.x, landingCoord.y, resultingState, config);
    }
    /**
     * Only called if piece is opponent's territory
     * If the condition to make a capture into the opponent's side are met
     * Captures and return the number of captured
     * Captures even if this could mean doing an illegal starvation
     */
    capture(x, y, state, config) {
        Utils.assert(this.coordIsInOpponentTerritory(x, y, state, config), 'AwaleRules.capture cannot capture the players house');
        let resultingState = state;
        let target = resultingState.getOptionalPieceAtXY(x, y);
        let capturedSum = 0;
        const captureMap = TableUtils.create(config.width, 2 * config.numberOfRows, 0);
        if (this.isCapturableValue(target.get()) === false) {
            // first space not capturable, we apply no change
            return { capturedSum: 0, captureMap, resultingState: state };
        }
        let direction = -1; // by defaut, capture from right to left
        let limit = -1;
        const player = state.getCurrentPlayer();
        if (player === Player.ONE) {
            /** if Player.ONE capture, it is on the bottom line
             * means capture goes from left to right ( + 1)
             * so one ending condition of the loop is reaching index MancalaState.WIDTH
             */
            direction = +1;
            limit = state.getWidth();
        }
        do {
            captureMap[y][x] = target.get(); // we addPart to the player score the captured seeds
            capturedSum += target.get();
            resultingState = resultingState.capture(player, new Coord(x, y));
            x += direction;
            target = resultingState.getOptionalPieceAtXY(x, y);
        } while ((x !== limit) && target.isPresent() && this.isCapturableValue(target.get()));
        return { capturedSum, captureMap, resultingState };
    }
    coordIsInOpponentTerritory(x, y, state, config) {
        if (state.isOnBoard(new Coord(x, y))) {
            return state.getCurrentPlayer() === Player.ZERO ?
                y < config.numberOfRows :
                config.numberOfRows <= y;
        }
        else {
            return false;
        }
    }
    captureIfLegal(x, y, state, config) {
        const player = state.getCurrentPlayer();
        const captureLessResult = {
            capturedSum: 0,
            resultingState: state, // Apply no capture
            captureMap: TableUtils.create(state.getWidth(), state.getHeight(), 0),
        };
        if (this.coordIsInOpponentTerritory(x, y, state, config)) {
            const captureResult = this.capture(x, y, state, config);
            const isStarving = MancalaRules.isStarving(player.getOpponent(), captureResult.resultingState.board, config);
            if (captureResult.capturedSum > 0 && isStarving) {
                /* if the distribution would capture all seeds
                 * the capture is forbidden and cancelled
                 */
                return captureLessResult;
            }
            else {
                return captureResult;
            }
        }
        else {
            return captureLessResult;
        }
    }
}
